import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import { timeLabel, useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import type { Business, OpeningHours } from '../store/seed';

const isEmptyOrValidUrl = (value: string) => !value || /^https?:\/\/[^\s.]+\.[^\s]+$/.test(value);
const isEmptyOrValidEmail = (value: string) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isEmptyOrValidPhone = (value: string) => !value || /^[+\d][\d\s()-]{6,}$/.test(value);

const SUNDAY_BEFORE_A_KNOWN_MONDAY = new Date(2026, 10, 15);
const POSTING_TIMEZONES = ['Europe/Warsaw', 'Europe/London', 'Europe/Helsinki', 'America/New_York'];

type BusinessTextField = 'name' | 'address' | 'phone' | 'email' | 'website' | 'menuUrl';

export default function BusinessEditor({
  onSaved,
  compact,
}: {
  onSaved?: () => void;
  compact?: boolean;
}) {
  const { translate, language, readOnlyProps, readOnly } = useApp();
  const { route, actor, notify, role } = useUi();
  const { commands } = useDemo();
  const { business, workspace, settings } = useWorkspaceData(route.workspaceId);
  const [draft, setDraft] = useState<Business>(business);
  const [exceptionDate, setExceptionDate] = useState('');
  const [exceptionNote, setExceptionNote] = useState('');
  const canEdit =
    role === 'owner' ||
    role === 'agency' ||
    (role === 'contributor' && !!settings.contributorCanEditBrand);
  const lockedForRole = !canEdit
    ? { disabled: true, title: translate('Only {name} can change this', { name: workspace.owner }) }
    : {};
  const weekdayName = (dayNumberFromMonday: number) => {
    const dateOnThatWeekday = new Date(SUNDAY_BEFORE_A_KNOWN_MONDAY);
    dateOnThatWeekday.setDate(SUNDAY_BEFORE_A_KNOWN_MONDAY.getDate() + dayNumberFromMonday);
    return new Intl.DateTimeFormat(language, { weekday: 'long' }).format(dateOnThatWeekday);
  };
  const setDraftField = <Key extends keyof Business>(key: Key, value: Business[Key]) =>
    setDraft({ ...draft, [key]: value });
  const setHours = (day: number, patch: Partial<OpeningHours>) =>
    setDraftField(
      'hours',
      draft.hours.map((hours) => (hours.day === day ? { ...hours, ...patch } : hours))
    );
  const invalidHours = draft.hours.filter(
    (hours) => !hours.closed && (!hours.open || !hours.close || hours.open >= hours.close)
  );
  const errors: Partial<Record<BusinessTextField, boolean>> = {
    website: !isEmptyOrValidUrl(draft.website),
    menuUrl: !isEmptyOrValidUrl(draft.menuUrl),
    email: !isEmptyOrValidEmail(draft.email),
    phone: !isEmptyOrValidPhone(draft.phone),
    address: !draft.address.trim(),
  };
  const invalid = Object.values(errors).some(Boolean) || invalidHours.length > 0;
  const changed = JSON.stringify(draft) !== JSON.stringify(business);

  function saveButtonHint() {
    if (invalid) return translate('Fix the highlighted fields first.');
    if (!changed) return translate('No changes yet');
    return undefined;
  }

  function save() {
    if (invalid || readOnly) return;
    commands.saveBusiness(workspace.id, draft, actor);
    notify(translate('Business details saved. AMY uses them in new drafts.'));
    onSaved?.();
  }

  const fieldErrorMessage = (key: BusinessTextField) => {
    if (key === 'address') return translate('Add the address guests should use.');
    if (key === 'email') return translate('Check the email address.');
    if (key === 'phone') return translate('Check the phone number.');
    return translate('Use a full link starting with https://');
  };

  const field = (key: BusinessTextField, label: string, hint?: string, type = 'text') => (
    <label className="wizard-field">
      {translate(label)}
      <input
        className="text-field"
        type={type}
        value={draft[key]}
        onChange={(event) => setDraftField(key, event.target.value)}
        aria-invalid={!!errors[key]}
        {...readOnlyProps}
        {...lockedForRole}
      />
      {errors[key] ? (
        <span className="field-error">{fieldErrorMessage(key)}</span>
      ) : (
        hint && <span className="muted small-print">{translate(hint)}</span>
      )}
    </label>
  );

  return (
    <div className={`business-editor ${compact ? 'compact' : ''}`}>
      <p className="muted small-print">
        <SafeIcon name="FiInfo" size={13} />{' '}
        {translate(
          'AMY only states facts from here, for example opening hours in a post. The brand kit links to these details instead of keeping its own copy.'
        )}
      </p>
      <div className="business-grid">
        {field('name', 'Business name')}
        {field('address', 'Address', 'Shown in posts that mention where to find you.')}
        {field('phone', 'Public phone', '', `tel`)}
        {field('email', 'Public email', '', 'email')}
        {field('website', 'Website', '', 'url')}
        {field(
          'menuUrl',
          'Menu or price list link',
          'Optional. AMY links to it instead of copying prices.',
          'url'
        )}
      </div>
      <fieldset className="hours-editor">
        <legend>{translate('Opening hours')}</legend>
        {draft.hours.map((hours) => (
          <div className="hours-row" key={hours.day}>
            <span className="hours-day">{weekdayName(hours.day)}</span>
            <label className="brand-check compact">
              <input
                type="checkbox"
                checked={!!hours.closed}
                onChange={(event) =>
                  setHours(hours.day, {
                    closed: event.target.checked,
                    open: event.target.checked ? '' : hours.open || '09:00',
                    close: event.target.checked ? '' : hours.close || '17:00',
                  })
                }
                {...readOnlyProps}
                {...lockedForRole}
              />{' '}
              {translate('Closed')}
            </label>
            {!hours.closed && (
              <>
                <input
                  className="text-field"
                  type="time"
                  aria-label={translate('{day} opens', { day: weekdayName(hours.day) })}
                  value={hours.open}
                  onChange={(event) => setHours(hours.day, { open: event.target.value })}
                  {...readOnlyProps}
                  {...lockedForRole}
                />
                <span aria-hidden="true">–</span>
                <input
                  className="text-field"
                  type="time"
                  aria-label={translate('{day} closes', { day: weekdayName(hours.day) })}
                  value={hours.close}
                  onChange={(event) => setHours(hours.day, { close: event.target.value })}
                  {...readOnlyProps}
                  {...lockedForRole}
                />
              </>
            )}
            {invalidHours.includes(hours) && (
              <span className="field-error">
                {translate('Closing time must be after opening time.')}
              </span>
            )}
          </div>
        ))}
      </fieldset>
      <fieldset className="hours-editor">
        <legend>{translate('Exceptions (holidays, events, closures)')}</legend>
        {draft.exceptions.length === 0 && (
          <p className="muted small-print">{translate('No exceptions yet.')}</p>
        )}
        {draft.exceptions.map((exception, exceptionIndex) => (
          <div className="hours-row" key={exception.date + exceptionIndex}>
            <strong>{exception.date}</strong>
            <span style={{ flex: 1 }}>{exception.note}</span>
            <button
              className="button small ghost"
              onClick={() =>
                setDraftField(
                  'exceptions',
                  draft.exceptions.filter((_, otherIndex) => otherIndex !== exceptionIndex)
                )
              }
              {...readOnlyProps}
              {...lockedForRole}
            >
              {translate('Remove')}
            </button>
          </div>
        ))}
        <div className="hours-row">
          <input
            className="text-field"
            placeholder={translate('Date, e.g. 31 Dec')}
            aria-label={translate('Exception date')}
            value={exceptionDate}
            onChange={(event) => setExceptionDate(event.target.value)}
            {...readOnlyProps}
            {...lockedForRole}
          />
          <input
            className="text-field"
            style={{ flex: 1 }}
            placeholder={translate('What changes, e.g. open 10–14')}
            aria-label={translate('What changes')}
            value={exceptionNote}
            onChange={(event) => setExceptionNote(event.target.value)}
            {...readOnlyProps}
            {...lockedForRole}
          />
          <button
            className="button small"
            disabled={!exceptionDate.trim() || !exceptionNote.trim() || readOnly || !canEdit}
            onClick={() => {
              setDraftField('exceptions', [
                ...draft.exceptions,
                { date: exceptionDate.trim(), note: exceptionNote.trim() },
              ]);
              setExceptionDate('');
              setExceptionNote('');
            }}
          >
            {translate('Add')}
          </button>
        </div>
      </fieldset>
      <div className="business-grid">
        <label className="wizard-field">
          {translate('Products and offers')}
          <textarea
            className="text-field"
            rows={2}
            value={draft.products}
            onChange={(event) => setDraftField('products', event.target.value)}
            {...readOnlyProps}
            {...lockedForRole}
          />
        </label>
        <label className="wizard-field">
          {translate('Who you want to reach')}
          <textarea
            className="text-field"
            rows={2}
            value={draft.audience}
            onChange={(event) => setDraftField('audience', event.target.value)}
            {...readOnlyProps}
            {...lockedForRole}
          />
        </label>
      </div>
      <label className="wizard-field">
        {translate('Posting time zone for {ws}', { ws: workspace.name })}
        <select
          className="select-field"
          value={settings.postingTimezone ?? 'Europe/Warsaw'}
          onChange={(event) =>
            commands.setWorkspacePrefs(workspace.id, { postingTimezone: event.target.value })
          }
          {...readOnlyProps}
          {...lockedForRole}
        >
          {POSTING_TIMEZONES.map((timezone) => (
            <option key={timezone} value={timezone}>
              {timezone.replace('_', ' ')}
            </option>
          ))}
        </select>
        <span className="muted small-print">
          {translate(
            'Posts go out at this local time. It does not change when someone views the calendar from another time zone.'
          )}
        </span>
      </label>
      <div className="button-row">
        <button
          className="button primary"
          onClick={save}
          disabled={readOnly || !canEdit || invalid || !changed}
          title={saveButtonHint()}
        >
          <SafeIcon name="FiCheck" />
          {translate('Save business details')}
        </button>
        {changed && (
          <button className="button ghost" onClick={() => setDraft(business)}>
            {translate('Discard changes')}
          </button>
        )}
        {business.updatedBy && (
          <span className="muted small-print">
            {translate('Last updated by {name} on {date}', {
              name: business.updatedBy,
              date: timeLabel(business.updatedAt ?? '', language, translate),
            })}
          </span>
        )}
      </div>
    </div>
  );
}
